import { useMemo } from "react";
import { useLingui } from "@lingui/macro";

import type { StudyGroup, SurvivalCurve, SurvivalEvent } from "@app/types/study";

import { formatNumber } from "./format";
import { groupColor } from "@app/lib/palette";

const WIDTH = 760;
const HEIGHT = 320;
const MARGIN = { top: 16, right: 20, bottom: 40, left: 52 };

interface Point {
  t: number;
  s: number;
  low: number | null;
  high: number | null;
}

/**
 * Kaplan-Meier curves drawn as steps — a survival estimate is flat between events, and a line
 * joining two steps would show a decline at a moment nobody had the event.
 *
 * For « délai jusqu'à l'intervention » the curve is flipped into the share already operated, which
 * is how a waiting time is read: rising from zero, not falling from one.
 *
 * Hand-drawn SVG like the board's representations: one scale for every group, the band of the 95 %
 * interval in the group's own colour at low opacity, and a tick wherever a patient left the
 * follow-up without the event (the reader's own patients only — on an aggregate cohort the curve
 * steps by month and individual departures are not drawn).
 */
export function SurvivalChart({
  groups,
  curves,
  event,
  grid,
  showBands,
}: {
  groups: StudyGroup[];
  curves: SurvivalCurve[];
  event: SurvivalEvent;
  grid: number[];
  /** The 95 % bands: clear with two groups, a wash of overlapping colour with five. */
  showBands: boolean;
}) {
  const { i18n, t } = useLingui();
  const locale = i18n.locale;
  const flip = event === "intervention";

  const series = useMemo(
    () =>
      curves.map((curve) => {
        const points: Point[] = [{ t: 0, s: 1, low: 1, high: 1 }, ...curve.curve];
        return points.map((p) =>
          flip
            ? {
                t: p.t,
                s: 1 - p.s,
                low: p.high === null ? null : 1 - p.high,
                high: p.low === null ? null : 1 - p.low,
              }
            : p,
        );
      }),
    [curves, flip],
  );

  const xMax = Math.max(
    1,
    grid.length ? grid[grid.length - 1] : 0,
    ...series.map((points) => (points.length ? points[points.length - 1].t : 0)),
  );
  const values = series.flatMap((points) =>
    points.flatMap((p) => (showBands ? [p.s, p.low ?? p.s, p.high ?? p.s] : [p.s])),
  );
  // A revision-free survival lives between 85 and 100 %: drawn on a full 0–100 % axis, every curve is
  // a flat line at the top. The axis starts at the tenth below the lowest bound, and says so.
  const yMin = flip ? 0 : Math.max(0, Math.floor((Math.min(1, ...values) - 0.02) * 10) / 10);
  const yMax = flip ? Math.min(1, Math.ceil((Math.max(0, ...values) + 0.02) * 10) / 10) : 1;
  const plotW = WIDTH - MARGIN.left - MARGIN.right;
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;
  const x = (t: number) => MARGIN.left + (Math.min(t, xMax) / xMax) * plotW;
  const y = (s: number) => MARGIN.top + ((yMax - s) / Math.max(1e-9, yMax - yMin)) * plotH;

  /** A step function's vertices: flat until the next time, then straight down (or up) to its value. */
  const stepVertices = (points: Point[], pick: (p: Point) => number | null): [number, number][] => {
    const vertices: [number, number][] = [];
    let previous: number | null = null;
    for (const point of points) {
      const value: number | null = pick(point) ?? previous;
      if (value === null) continue;
      if (previous !== null) vertices.push([x(point.t), y(previous)]);
      vertices.push([x(point.t), y(value)]);
      previous = value;
    }
    return vertices;
  };
  const toPath = (vertices: [number, number][]) =>
    vertices.map(([vx, vy], i) => `${i === 0 ? "M" : "L"}${vx},${vy}`).join("");
  const band = (points: Point[]) => {
    const upper = stepVertices(points, (p) => p.high);
    const lower = stepVertices(points, (p) => p.low).reverse();
    if (!upper.length || !lower.length) return "";
    return `${toPath([...upper, ...lower])}Z`;
  };

  const yTicks: number[] = [];
  for (let v = Math.round(yMin * 10); v <= Math.round(yMax * 10); v += 1) yTicks.push(v / 10);
  // The risk table stops where few remain at risk; the axis goes on as far as the curves do.
  const step = grid.length > 1 ? grid[1] - grid[0] : xMax <= 36 ? 6 : 12;
  const xTicks: number[] = [];
  for (let month = 0; month <= xMax; month += step) xTicks.push(month);

  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-auto w-full" role="img">
      {yTicks.map((tick) => (
        <g key={tick}>
          <line x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={y(tick)} y2={y(tick)} stroke="#E2E8F0" />
          <text x={MARGIN.left - 8} y={y(tick)} dy="0.32em" textAnchor="end" fontSize="11" fill="#64748B">
            {formatNumber(tick * 100, locale, 0)}&nbsp;%
          </text>
        </g>
      ))}
      {xTicks.map((month) => (
        <g key={month}>
          <line x1={x(month)} x2={x(month)} y1={HEIGHT - MARGIN.bottom} y2={HEIGHT - MARGIN.bottom + 4} stroke="#94A3B8" />
          <text x={x(month)} y={HEIGHT - MARGIN.bottom + 16} textAnchor="middle" fontSize="11" fill="#64748B">
            {month}
          </text>
        </g>
      ))}
      <line
        x1={MARGIN.left}
        x2={WIDTH - MARGIN.right}
        y1={HEIGHT - MARGIN.bottom}
        y2={HEIGHT - MARGIN.bottom}
        stroke="#94A3B8"
      />
      <text x={(MARGIN.left + WIDTH - MARGIN.right) / 2} y={HEIGHT - 6} textAnchor="middle" fontSize="11" fill="#64748B">
        {t`Mois`}
      </text>
      {series.map((points, index) => (
        <g key={groups[index]?.id ?? index}>
          {showBands && <path d={band(points)} fill={groupColor(index)} opacity={0.12} />}
          <path d={toPath(stepVertices(points, (p) => p.s))} fill="none" stroke={groupColor(index)} strokeWidth={2} />
          {curves[index].curve
            .filter((p) => (p.censored ?? 0) > 0)
            .map((p) => (
              <line
                key={`${index}-${p.t}`}
                x1={x(p.t)}
                x2={x(p.t)}
                y1={y(flip ? 1 - p.s : p.s) - 4}
                y2={y(flip ? 1 - p.s : p.s) + 4}
                stroke={groupColor(index)}
                strokeWidth={1}
              />
            ))}
        </g>
      ))}
    </svg>
  );
}
